import { Add, Visibility } from "@mui/icons-material";
import { Button, Stack, Tooltip, Grid2 as Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { DataSheetPDFPreview, paperSizes, SectionPaper } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch } from "../../../redux";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { reorder } from "../../../utils";
import { orientationOptions } from "../../documentTemplates/dashboard/addEditTemplate";
import { CheckListCategories, CheckListContext, CheckListData } from "./../viewCheckListDetail";
import { CategoryListItem } from "./categoryListItem";
import { ExcelImporter, ExcelImportState } from "./excelImporter";
import { closeConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { useSearchParams } from "react-router-dom";
import { prepareDataTableBody } from "../../../redux/services";

export const tableLimit = 15;

export const Categories: React.FC<{ children?: JSX.Element, dataTable?: CheckListData, isPublished?: boolean }> = ({ dataTable, isPublished = false }) => {

    const { state, updateState } = React.useContext(CheckListContext);

    const { enqueueSnackbar } = useSnackbar();

    const [searchParams] = useSearchParams();

    const isForm = searchParams.get("mode") ? true : false

    const dispatch = useAppDispatch();

    const [paperSize] = React.useState<SelectBoxOption | null>(paperSizes[6]);
    const [orientation] = React.useState<SelectBoxOption | null>(orientationOptions[0]);
    const [showRuler] = React.useState(false);

    const addCategory = () => {
        let categoryLength = state.categories?.length ?? 0;
        if (categoryLength >= tableLimit) {
            enqueueSnackbar(
                `You can only have up to ${tableLimit} tables. Adding more isn't allowed.`,
                { variant: "warning" }
            );
        } else {
            let newCategories: CheckListCategories[] = [
                ...(state.categories ?? []),
                {
                    name: `Table ${categoryLength + 1}`,
                    description: '',
                    header_color: 'rgb(230, 229, 229)',
                    definition: {
                        columns: [
                            {
                                field: 'column',
                                headerName: 'Column 1',
                                headerClassName: `header-${0}`,
                                width: 230,
                                editable: true
                            }
                        ],
                        rows: []
                    },
                    position: categoryLength + 1
                }
            ];
            updateState('categories', [...newCategories]);
        }
    };

    const onDragEnd = (result: any) => {
        if (!result.destination) {
            return;
        }
        if (result.source.index === result.destination.index) {
            return;
        }

        const newCategories: CheckListCategories[] = reorder(
            state.categories,
            result.source.index,
            result.destination.index,
        );
        updateState('categories', [...newCategories]);
    };

    const generatePreviewHandler = () => {
        const title = dataTable?.data?.name ?? state?.name ?? "Data Sheet Preview";
        const updatedDataTable = dataTable ?? { data: state, isLoading: false, isFetching: false, isError: false, isSuccess: true };
        dispatch(
            openDialog({
                title: "",
                hideNegativeBtn: true,
                hideCloseIcon: true,
                maxWidth: "lg",
                enablePadding: false,
                positiveBtn: "Close",
                body: <Stack
                    sx={{
                        p: 1,
                        height: "87vh"
                    }}><DataSheetPDFPreview type="datasheet" title={title} dataSheet={dataTable ? dataTable : { ...updatedDataTable, data: prepareDataTableBody(updatedDataTable?.data, "template") }} selectedSize={paperSize || undefined} selectedOrientation={orientation || undefined} /></Stack>,
                onOk: () => dispatch(closeDialog())
            })
        );
    };

    // const onChangeSwitch = (e: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    //     setShowRuler(isChecked);
    // };

    const addTablesFromExcel = (excelImport: ExcelImportState[]) => {
        let categoryLength = state.categories?.length ?? 0;
        if (categoryLength >= tableLimit) {
            enqueueSnackbar(
                `You are having ${categoryLength} tables, you can only have up to ${tableLimit} tables. Adding more isn't allowed.`,
                { variant: "warning" }
            );
        } else if ((excelImport.length + categoryLength) > tableLimit) {
            enqueueSnackbar(
                `You are having ${categoryLength} tables, you can only have up to ${tableLimit} tables. Adding ${excelImport.length} more isn't allowed.`,
                { variant: "warning" }
            );
        } else {
            let newCategories: CheckListCategories[] = [
                ...(state.categories ?? []),
                ...excelImport.map((xImp, index) => {
                    return {
                        name: `${xImp.name}`,
                        description: '',
                        header_color: 'rgb(230, 229, 229)',
                        definition: {
                            columns: Object.keys(xImp.data[0] ?? {}).map((key, index) => ({
                                field: `${key}`,
                                headerName: `${key}`,
                                headerClassName: `header-${0}`,
                                width: 230,
                                editable: true
                            })),
                            rows: xImp.data.map((_, index: number) => ({ ..._, id: `${index}` }))
                        },
                        position: (state.categories ?? []).length + index + 1
                    }
                }),

            ];
            updateState('categories', [...newCategories]);
            dispatch(closeConfirmationDialog());
            dispatch(closeDialog());
        }
    };

    const onImportBtnClicked = () => {
        dispatch(
            openDialog({
                title: "",
                hideCloseIcon: true,
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                maxWidth: "lg",
                enablePadding: false,
                body: <ExcelImporter onDataOk={addTablesFromExcel} tableLimit={tableLimit} currentCategoryLimit={state.categories?.length ?? 0} />
            })
        );
    };

    return (
        (<SectionPaper
            title="Tables"
            stack
            renderButtons={[
                <Grid spacing={2} justifyContent={"flex-end"} alignItems={"center"} container>
                    {/* <Grid size={{
                        xs: 12,
                        md: isForm ? 6 : 4,
                        lg: "auto"
                    }}>
                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                justifyContent: "flex-start"
                            }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontFamily: "htrts_medium"
                                }}>
                                Show Ruler
                            </Typography>
                            <AntSwitch sx={{ ml: 2 }} size="small" color="primary" checked={showRuler} onChange={onChangeSwitch} />
                        </Stack>
                    </Grid>
                    <Grid size={{
                        xs: 12,
                        md: isForm ? 6 : 4,
                        lg: "auto"
                    }}>

                        <HighlightSelectBox
                            label={"Paper Size"}
                            margin={"none"}
                            fullWdith
                            value={paperSize}
                            options={paperSizes}
                            onChange={(option: SelectBoxOption) => { setPaperSize(option) }}
                        />
                    </Grid>
                    <Grid size={{
                        xs: 12,
                        md: isForm ? 6 : 4,
                        lg: "auto"
                    }}>

                        <HighlightSelectBox
                            label={"Orientation"}
                            margin={"none"}
                            fullWdith
                            value={orientation}
                            options={orientationOptions}
                            onChange={(option: SelectBoxOption) => { setOrientation(option) }}
                        />
                    </Grid> */}
                    <Grid size={{
                        xs: 12,
                        md: isForm ? 6 : 4,
                        lg: "auto"
                    }}>
                        <Tooltip title={"Generate Preview"} arrow placement="top">
                            <Button
                                fullWidth
                                disabled={dataTable?.isLoading || dataTable?.isFetching}
                                color="primary"
                                variant="outlined"
                                onClick={generatePreviewHandler}
                                endIcon={<Visibility />}
                            >
                                Preview
                            </Button>
                        </Tooltip>
                    </Grid>
                    {!isPublished &&
                        <>
                            <Grid size={{
                                xs: 12,
                                md: isForm ? 6 : 4,
                                lg: "auto"
                            }}>
                                <Tooltip title={"Add Table"} arrow placement="top">
                                    <Button
                                        fullWidth
                                        color="primary"
                                        variant={"contained"}
                                        onClick={() => addCategory()}
                                        endIcon={<Add />}
                                    >
                                        Add
                                    </Button>
                                </Tooltip>
                            </Grid>
                            <Grid size={{
                                xs: 12,
                                md: isForm ? 6 : 4,
                                lg: "auto"
                            }}>
                                <Tooltip title={"Import Tables From Excel"} arrow placement="top">
                                    <Button
                                        fullWidth
                                        variant={"contained"}
                                        onClick={() => onImportBtnClicked()}
                                        endIcon={<img style={{ marginLeft: "8px" }} src={'/beta.gif'} alt='In Beta' />}
                                    >
                                        Import
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </>
                    }
                </Grid>
            ]}
        >
            {/* Details */}
            < DragDropContext onDragEnd={onDragEnd} >
                <Droppable
                    droppableId="categoryList"
                    mode="virtual"
                >
                    {(droppableProvided: any) => (
                        <Stack spacing={1} ref={droppableProvided.innerRef} sx={{ overflow: "scroll" }}>
                            {state.categories?.map((category, key: number) => {
                                return <CategoryListItem isPublished={isPublished} category={category} index={key} key={key} size={paperSize} orientation={orientation} showRuler={showRuler} />
                            })}
                            {droppableProvided.placeholder}
                        </Stack>
                    )}
                </Droppable>
            </DragDropContext >
        </SectionPaper >)
    );
};